// Considered: the editor's former "If no related spheres match" CollapsibleSectionAtom + plain dropdown — rejected
// because it offered four colour-named options and said nothing about which lens the factor reads through.
// Read-through is one DropdownAtom (follows the lens + perspectives); PerspectivePickerMolecule stays for its other consumers.
// See spec Reuse Audit.
import React, { useId } from 'react';
import { useTranslation } from 'localization';
import type { StatusValue } from 'types/spherePoints';
import DropdownAtom, { type DropdownChangeEvent } from '../../atoms/Dropdown/DropdownAtom';
import type { PerspectiveOption } from '../PerspectiveFilter/PerspectiveFilterMolecule';
import { getLocalizedStatusLabel } from '../../../utils/statusColors';
import RollupStatusOptionLabel from './RollupStatusOptionLabel';
import './RollupEdgeCasesSection.scss';

// The read-through dropdown's value for "no pin"; it never leaves this file (the form stores `null`).
const FOLLOWS_LENS = '__follows_lens__';

export interface RollupEdgeCasesSectionProps {
  emptyBehavior: StatusValue;
  /** The pinned perspective's system name; `null` means the factor follows the active lens. */
  childPerspective: string | null;
  /** The perspectives a factor can be pinned to; also names the lens the set card currently reads through. */
  availablePerspectives: PerspectiveOption[];
  /** The set card's current lens (`rollupSet.perspectiveSystemName`). */
  lensSystemName?: string;
  onChange: (updates: { emptyBehavior?: StatusValue; childPerspective?: string | null }) => void;
  disabled?: boolean;
  className?: string;
}

// Unknown leads: it is the safest answer when nothing is there to read.
const EMPTY_STATUSES: StatusValue[] = ['unknown', 'on_track', 'at_risk', 'needs_attention', 'critical', 'blocked'];

const RollupEdgeCasesSection: React.FC<RollupEdgeCasesSectionProps> = ({
  emptyBehavior,
  childPerspective,
  availablePerspectives,
  lensSystemName,
  onChange,
  disabled = false,
  className,
}) => {
  const { t } = useTranslation();
  const uid = useId();
  const statusLabel = (status: StatusValue) => getLocalizedStatusLabel(status, t as (key: string) => string);
  const statusOptions = EMPTY_STATUSES.map((value) => ({ value, label: statusLabel(value) }));
  const labelOf = (systemName: string | null | undefined) => availablePerspectives.find((p) => p.systemName === systemName)?.label;

  const pinnedMissing = childPerspective !== null && !labelOf(childPerspective);
  const lensLabel = labelOf(lensSystemName);

  let readThroughHelp: string;
  if (childPerspective === null) {
    readThroughHelp = lensLabel
      ? t('statusFactors.edgeCases.readThrough.followsHelp', 'follows the active lens ({{lens}}) unless overridden', {
          lens: lensLabel,
        })
      : t('statusFactors.edgeCases.readThrough.followsHelpNoLens', 'follows the active lens unless overridden');
  } else if (pinnedMissing) {
    readThroughHelp = t('statusFactors.edgeCases.readThrough.pinnedMissing', 'The pinned perspective no longer exists — choose another or follow the lens.');
  } else {
    readThroughHelp = t('statusFactors.edgeCases.readThrough.pinnedHelp', 'pinned to {{perspective}} regardless of the active lens', {
      perspective: labelOf(childPerspective),
    });
  }

  const readThroughOptions = [
    { value: FOLLOWS_LENS, label: t('statusFactors.edgeCases.readThrough.follows', 'follows lens') },
    ...availablePerspectives.map((p) => ({ value: p.systemName, label: p.label })),
    ...(pinnedMissing && childPerspective !== null
      ? [
          {
            value: childPerspective,
            label: t('statusFactors.edgeCases.readThrough.stalePin', 'A perspective that no longer exists'),
          },
        ]
      : []),
  ];

  const emptyLabelId = `${uid}-empty-label`;
  const readLabelId = `${uid}-read-label`;
  const classes = ['rollup-edge-cases-section', className].filter(Boolean).join(' ');

  return (
    <section className={classes} aria-labelledby={`${uid}-heading`} data-testid="rollup-edge-cases-section">
      <h3 id={`${uid}-heading`} className="rollup-edge-cases-section__heading">
        {t('statusFactors.edgeCases.heading', 'Edge cases')}
      </h3>

      <div className="rollup-edge-cases-section__body">
        <div className="rollup-edge-cases-section__row" data-testid="rollup-empty-population">
          <div className="rollup-edge-cases-section__text">
            <label id={emptyLabelId} htmlFor={`${uid}-empty`} className="rollup-edge-cases-section__label">
              {t('statusFactors.edgeCases.empty.label', 'If the population is empty')}
            </label>
            <p className="rollup-edge-cases-section__help">
              {t('statusFactors.edgeCases.empty.help', "a team with no open risks: nothing wrong, or nobody's looked?")}
            </p>
          </div>
          <DropdownAtom
            dropdownSize="compact"
            inputId={`${uid}-empty`}
            aria-labelledby={emptyLabelId}
            value={emptyBehavior}
            onChange={(e: DropdownChangeEvent) => onChange({ emptyBehavior: e.value as StatusValue })}
            options={statusOptions}
            optionLabel="label"
            optionValue="value"
            valueTemplate={(option: { value: StatusValue } | null) => (option ? <RollupStatusOptionLabel status={option.value} /> : null)}
            itemTemplate={(option: { value: StatusValue }) => <RollupStatusOptionLabel status={option.value} />}
            disabled={disabled}
          />
        </div>

        <div className="rollup-edge-cases-section__row" data-testid="rollup-read-through">
          <div className="rollup-edge-cases-section__text">
            <label id={readLabelId} htmlFor={`${uid}-read`} className="rollup-edge-cases-section__label">
              {t('statusFactors.edgeCases.readThrough.label', "Read each member's status through")}
            </label>
            <p className="rollup-edge-cases-section__help" data-testid="rollup-read-through-help">
              {readThroughHelp}
            </p>
          </div>
          <DropdownAtom
            dropdownSize="compact"
            inputId={`${uid}-read`}
            aria-labelledby={readLabelId}
            value={childPerspective ?? FOLLOWS_LENS}
            onChange={(e: DropdownChangeEvent) => onChange({ childPerspective: e.value === FOLLOWS_LENS ? null : (e.value as string) })}
            options={readThroughOptions}
            optionLabel="label"
            optionValue="value"
            disabled={disabled}
          />
        </div>
      </div>
    </section>
  );
};

RollupEdgeCasesSection.displayName = 'RollupEdgeCasesSection';

export default RollupEdgeCasesSection;
